// Shared constants/components used by both IpdLabOrders.js (the orders table)
// and LabReportModal.js (the generated report view) — kept in one place so
// neither file has to import from the other.
import React from "react";

// Downstream readers (phlebotomist board, doctor Order Management) treat a
// report as published only once the lab user has finalized it — not when the
// bench merely marks the test Completed.
export const REPORT_PUBLISHED_STATUSES = ["Reported", "Verified"];

/** @deprecated use REPORT_PUBLISHED_STATUSES — kept for existing imports */
export const READY_STATUSES = REPORT_PUBLISHED_STATUSES;

// ── Generated report memory ───────────────────────────────────────────────────
// The parameter/result rows a lab user types into the Initial Observation panel
// aren't persisted by any endpoint yet, so a generated report is remembered
// here, keyed by order_no. localStorage (not sessionStorage like the batch
// memory above) because the report has to outlive the tab it was typed in —
// the phlebotomist's "View" and the doctor's Order Management "View" both read
// it back later.
const LAB_REPORT_STORAGE_KEY = "ipd_lab_reports_v1";
const LAB_REPORT_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // keep a month

const readLabReportStore = () => {
  try {
    const raw = localStorage.getItem(LAB_REPORT_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const writeLabReportStore = (store) => {
  try {
    localStorage.setItem(LAB_REPORT_STORAGE_KEY, JSON.stringify(store));
  } catch {
    /* quota — best-effort */
  }
};

// Initial Observation panels → flat report rows. Shared so the report modal
// and the stored copy always show the same table.
export function flattenObservationPanels(panels) {
  if (!Array.isArray(panels)) return [];
  const rows = [];
  panels.forEach((panel) => {
    (panel?.tests || []).forEach((t) => {
      if (!t?.value) return;
      rows.push({
        param: t.test,
        value: t.value,
        unit: t.unit || "",
        // Typed per test in the Initial Observation panel; em dash only when the
        // technician left it blank.
        refRange: t.refRange || "—",
      });
    });
  });
  return rows;
}

/**
 * Store what the bench entered for one order.
 *
 * `panels` is the Initial Observation panel structure and `rows` its flattened
 * view. BOTH are kept: rows are what the report table renders, panels are what
 * the editor needs to reopen. Storing only rows is what used to make a report
 * un-editable the moment the modal was closed — the table came back from the
 * store, but there were no panels left to edit, so the "N panels added" chip
 * that reopens the editor never rendered.
 */
export function rememberLabReport(
  orderNo,
  { rows = [], service = "", panels = [], finalized = false } = {},
) {
  const key = String(orderNo || "").trim();
  if (!key) return;

  const store = readLabReportStore();
  const now = Date.now();
  // Drop anything stale on the way past so the store can't grow forever.
  Object.keys(store).forEach((k) => {
    if (now - (store[k]?.generatedAt || 0) > LAB_REPORT_MAX_AGE_MS) {
      delete store[k];
    }
  });

  // A panel added but not yet typed into produces zero rows — that still has to
  // survive, or the technician loses the panel they just picked.
  if (rows.length === 0 && panels.length === 0) delete store[key];
  else
    store[key] = {
      rows,
      service,
      panels,
      generatedAt: now,
      finalized: !!finalized,
    };

  writeLabReportStore(store);
}

/**
 * Every order's stored observation panels, keyed by order number — used to seed
 * the dashboard's `observations` state so results stay editable across a
 * reload or a reopened modal.
 */
export function readLabObservationPanels() {
  const store = readLabReportStore();
  const out = {};
  Object.keys(store).forEach((orderNo) => {
    const panels = store[orderNo]?.panels;
    if (Array.isArray(panels) && panels.length > 0) out[orderNo] = panels;
  });
  return out;
}

/** Observation panels previously stored for one order (empty array if none). */
export function readLabReportPanels(orderNo) {
  const store = readLabReportStore();
  for (const key of labReportOrderNoKeys(orderNo)) {
    const panels = store[key]?.panels;
    if (Array.isArray(panels) && panels.length > 0) return panels;
  }
  return [];
}

/**
 * Rebuild an editable observation panel out of flat report rows.
 *
 * Reports written by an older build kept only `rows`, so reopening one left
 * nothing for the editor to work on and the results were effectively frozen.
 * Turning those rows back into a single panel means Amend still works instead
 * of forcing the technician to re-pick the panel and retype every value.
 */
export function panelsFromReportRows(rows, panelName = "Recorded Results") {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  return [
    {
      panelOptionId: "restored-results",
      panel_name: panelName || "Recorded Results",
      tests: rows.map((r, i) => ({
        id: `restored-${i}`,
        test: r.param,
        code: "-",
        value: r.value ?? "",
        unit: r.unit || "",
        // flattenObservationPanels writes an em dash when nothing was typed —
        // don't feed that back into the input as if it were a real range.
        refRange: r.refRange && r.refRange !== "—" ? r.refRange : "",
      })),
    },
  ];
}

const labReportOrderNoKeys = (orderNo) => {
  const key = String(orderNo || "").trim();
  if (!key) return [];
  const keys = [key];
  if (/^BC-/i.test(key)) keys.push(key.slice(3));
  else keys.push(`BC-${key}`);
  return [...new Set(keys)];
};

/** Flatten API / stored result rows into the report table shape. */
export const normalizeLabResultRows = (raw) => {
  if (!Array.isArray(raw) || raw.length === 0) return [];
  return raw
    .map((r) => {
      if (r == null || typeof r !== "object") return null;
      const param =
        r.param ||
        r.parameter ||
        r.test_name ||
        r.test ||
        r.name ||
        r.analyte ||
        "";
      const value =
        r.value ?? r.result ?? r.observed_value ?? r.result_value ?? "";
      if (!String(param).trim() && (value === "" || value == null)) return null;
      return {
        param: String(param || "Result").trim() || "Result",
        value: value === "" || value == null ? "" : String(value),
        unit: r.unit || r.units || "",
        refRange:
          r.refRange || r.reference_range || r.ref_range || r.range || "—",
      };
    })
    .filter((r) => r && String(r.value).trim() !== "");
};

/** Report rows previously stored for one order, or null. */
export function readLabReport(orderNo) {
  const store = readLabReportStore();
  for (const key of labReportOrderNoKeys(orderNo)) {
    const entry = store[key];
    if (!entry) continue;
    if (Array.isArray(entry.rows) && entry.rows.length > 0) return entry;
    const fromPanels = flattenObservationPanels(entry.panels);
    if (fromPanels.length > 0) return { ...entry, rows: fromPanels };
  }
  return null;
}

export const isReportGenerated = (order) => {
  if (REPORT_PUBLISHED_STATUSES.includes(order?.status)) return true;
  const stored = readLabReport(order?.orderNo);
  return !!stored?.finalized;
};

/** Observation panels to reopen a report for one order (store, then rows). */
export function labReportObservationsForOrder(orderNo, order) {
  const key = String(orderNo || order?.orderNo || "").trim();
  if (!key) return {};
  for (const k of labReportOrderNoKeys(key)) {
    const panels = readLabReportPanels(k);
    if (panels.length > 0) return { [key]: panels };
  }
  const report = readLabReport(key);
  if (report?.rows?.length) {
    return { [key]: panelsFromReportRows(report.rows, order?.service) };
  }
  const fromOrder = normalizeLabResultRows(
    order?.results || order?.test_results || order?.parameters,
  );
  if (fromOrder.length > 0) {
    return { [key]: panelsFromReportRows(fromOrder, order?.service) };
  }
  return {};
}

// ── Client batch memory ───────────────────────────────────────────────────────
// One Care Plan / Order Management save = one lab_test_batch_id. The backend
// still mints a unique LAB- number per test and may not echo the batch id on
// get_ipd_lab_orders*, so we remember each save in sessionStorage and re-apply
// it when building the lab dashboard rows. A later save for the same patient
// gets a new batch id → its own patient row.
const LAB_BATCH_STORAGE_KEY = "ipd_lab_order_batches_v1";
const LAB_BATCH_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // keep a week

export const createLabOrderBatchId = () =>
  `ltb-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const readLabBatchStore = () => {
  try {
    const raw = sessionStorage.getItem(LAB_BATCH_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const writeLabBatchStore = (store) => {
  try {
    sessionStorage.setItem(LAB_BATCH_STORAGE_KEY, JSON.stringify(store));
  } catch {
    /* quota — best-effort */
  }
};

/** Call after a successful add_patient_lab_tests/ POST (Care Plan or Order Mgmt). */
export function rememberLabOrderBatch(admissionId, batchId, tests = []) {
  const adm = String(admissionId || "").trim();
  const bid = String(batchId || "").trim();
  if (!adm || !bid) return;

  const services = (tests || [])
    .map((t) => ({
      medical_service_id: String(t?.medical_service_id ?? t?.id ?? "").trim(),
      name: String(t?.name || t?.service || "")
        .trim()
        .toLowerCase(),
    }))
    .filter((t) => t.medical_service_id || t.name);
  if (services.length === 0) return;

  const store = readLabBatchStore();
  const list = Array.isArray(store[adm]) ? store[adm] : [];
  const now = Date.now();
  const next = [
    { batchId: bid, createdAt: now, services },
    ...list.filter(
      (b) =>
        b &&
        b.batchId !== bid &&
        now - (b.createdAt || 0) < LAB_BATCH_MAX_AGE_MS,
    ),
  ].slice(0, 40);
  store[adm] = next;
  writeLabBatchStore(store);
}

/** Admission ids with a client-remembered save in the last `maxAgeMs` (default 15m). */
export function listRecentRememberedAdmissionIds(maxAgeMs = 15 * 60 * 1000) {
  const store = readLabBatchStore();
  const now = Date.now();
  return Object.keys(store).filter((adm) => {
    const batches = Array.isArray(store[adm]) ? store[adm] : [];
    return batches.some((b) => now - (b.createdAt || 0) < maxAgeMs);
  });
}

/**
 * Stamp remembered batch ids onto admission orders before time-window reconcile.
 * 1) Match by medical_service_id / service name (newest order wins per service).
 * 2) Fill any remaining slots from the same save by ordered-time proximity to
 *    createdAt — so a 4-test Sign & Send still becomes ONE patient row even when
 *    names/ids from the API don't perfectly match what we posted.
 */
export function applyRememberedLabBatches(admissionId, orders = []) {
  const adm = String(admissionId || "").trim();
  const list = (orders || []).map((o) => ({ ...o }));
  if (!adm || list.length === 0) return list;

  const store = readLabBatchStore();
  const batches = Array.isArray(store[adm]) ? store[adm] : [];
  if (batches.length === 0) return list;

  const TIME_SLACK_MS = 2 * 60 * 1000; // ±2 min around the save

  const used = new Set();
  const takeMatch = (service) => {
    const sid = String(service.medical_service_id || "").trim();
    const name = String(service.name || "")
      .trim()
      .toLowerCase();
    let bestIdx = -1;
    let bestTime = -Infinity;
    list.forEach((order, i) => {
      if (used.has(i) || order.batchId) return;
      const oid = String(
        order.medicalServiceId || order.medical_service_id || "",
      ).trim();
      const oname = String(order.service || order.name || "")
        .trim()
        .toLowerCase();
      const idMatch = sid && oid && sid === oid;
      const nameMatch = name && oname && name === oname;
      if (!idMatch && !nameMatch) return;
      const t = Date.parse(order.ordered) || 0;
      if (t >= bestTime) {
        bestTime = t;
        bestIdx = i;
      }
    });
    if (bestIdx < 0) return null;
    used.add(bestIdx);
    return list[bestIdx];
  };

  const takeNearestUnbatched = (createdAt) => {
    let bestIdx = -1;
    let bestDelta = Infinity;
    list.forEach((order, i) => {
      if (used.has(i) || order.batchId) return;
      const t = Date.parse(order.ordered) || 0;
      if (!t || !createdAt) return;
      const delta = Math.abs(t - createdAt);
      if (delta > TIME_SLACK_MS) return;
      if (delta < bestDelta) {
        bestDelta = delta;
        bestIdx = i;
      }
    });
    if (bestIdx < 0) return null;
    used.add(bestIdx);
    return list[bestIdx];
  };

  // Newest remembered save first so a later re-order of the same test claims
  // the newest unmatched rows, not the older save's leftover.
  [...batches]
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
    .forEach((batch) => {
      if (!batch?.batchId || !Array.isArray(batch.services)) return;
      let matched = 0;
      batch.services.forEach((svc) => {
        const match = takeMatch(svc);
        if (match) {
          match.batchId = batch.batchId;
          matched += 1;
        }
      });
      // Same POST, incomplete name/id match — claim remaining nearby orders
      // up to the posted test count so Blood + Urine from one save stay together.
      const need = batch.services.length - matched;
      for (let n = 0; n < need; n += 1) {
        const fill = takeNearestUnbatched(batch.createdAt);
        if (!fill) break;
        fill.batchId = batch.batchId;
      }
    });

  return list;
}

// Grouped shape from add_patient_lab_tests/ / future get_patient_lab_tests/:
//   { care_context_id, admission_id, patient_type, doctor_name, status,
//     test: [{ name, code, medical_service_id }] }
const isGroupedLabTestEntry = (item) => {
  const nestedTests = item?.test;
  if (!Array.isArray(nestedTests) || nestedTests.length === 0) return false;
  const first = nestedTests[0];
  return typeof first === "object" && first !== null && "name" in first;
};

const buildLabTestBatchId = (item) =>
  item?.lab_test_batch_id ||
  item?.batch_id ||
  item?.lab_test_id ||
  item?.order_no ||
  `${item?.care_context_id || "lt"}-${item?.created_at || item?.appointment_start_time || ""}`;

// Pull lab_test_list out of whatever envelope the backend returns.
export function extractPatientLabTestsFromResponse(data) {
  if (!data) return [];
  if (isGroupedLabTestEntry(data)) {
    return normalizePatientLabTestList([data]);
  }
  if (Array.isArray(data.lab_test_list)) {
    return normalizePatientLabTestList(data.lab_test_list);
  }
  if (Array.isArray(data.data)) {
    return normalizePatientLabTestList(data.data);
  }
  if (Array.isArray(data)) {
    return normalizePatientLabTestList(data);
  }
  return [];
}

// Flatten grouped test[] entries into one legacy-compatible row per test,
// preserving batchId so tests from the same doctor order stay in one UI row.
export function normalizePatientLabTestList(labTestList) {
  if (!Array.isArray(labTestList)) return [];

  const flat = [];
  labTestList.forEach((item) => {
    if (!item || typeof item !== "object") return;

    if (isGroupedLabTestEntry(item)) {
      const batchId = buildLabTestBatchId(item);
      item.test.forEach((t, idx) => {
        const dedupeKey = t.medical_service_id || t.code || t.name || idx;
        flat.push({
          ...item,
          batchId,
          lab_test_batch_id: batchId,
          test_name: t.name,
          name: t.name,
          code: t.code || "",
          snomed_code: t.code || "",
          medical_service_id: t.medical_service_id || "",
          lab_test_id: item.lab_test_id
            ? `${item.lab_test_id}-${dedupeKey}`
            : `${item.care_context_id || "lt"}-${dedupeKey}`,
        });
      });
      return;
    }

    flat.push({
      ...item,
      batchId: item.batchId || buildLabTestBatchId(item),
      lab_test_batch_id: item.lab_test_batch_id || buildLabTestBatchId(item),
    });
  });

  return flat;
}

// ── Unified get_patient_lab_tests/ (paginated All/OPD/IPD board) ───────────────

const DEFAULT_ORDER_STATUS = "Ordered";
const DEFAULT_PRIORITY = "Routine";

/** "07/09/2026, 07:00:00 AM" (UTC) → ISO with Z; otherwise returns the input string. */
export const parseOrderedTime = (str) => {
  if (!str) return null;
  const m =
    /^(\d{2})\/(\d{2})\/(\d{4}),?\s*(\d{2}):(\d{2}):(\d{2})\s*(AM|PM)$/i.exec(
      String(str).trim(),
    );
  if (!m) return str;
  const [, mm, dd, yyyy, hh12, min, ss, ampm] = m;
  let hh = parseInt(hh12, 10) % 12;
  if (/pm/i.test(ampm)) hh += 12;
  const pad = (n) => String(n).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}T${pad(hh)}:${min}:${ss}Z`;
};

export const normalizePriorityLabel = (priority) => {
  if (!priority) return DEFAULT_PRIORITY;
  const lower = String(priority).trim().toLowerCase();
  if (lower === "stat") return "STAT";
  if (lower === "urgent") return "Urgent";
  if (lower === "routine") return "Routine";
  return String(priority);
};

/** Map backend order status into the board's Ordered→Verified vocabulary. */
export const normalizeLabOrderStatus = (status) => {
  if (!status) return DEFAULT_ORDER_STATUS;
  const value = String(status).trim();
  const upper = value.toUpperCase();
  // Billing-side placement values, not the lab lifecycle. "Sample Collected"
  // is a real phlebotomy step and must not be collapsed back to Ordered.
  if (upper === "NEW" || upper === "PAID") {
    return DEFAULT_ORDER_STATUS;
  }
  if (upper === "IN PROGRESS" || upper === "IN_PROGRESS") {
    return "Processing";
  }
  return value;
};

/** Nested order line from get_patient_lab_tests/ (or legacy IPD endpoints). */
export const mapApiOrder = (o, { batchId } = {}) => {
  const orderNo = String(o.order_no ?? o.orderNo ?? "");
  return {
    orderNo,
    batchId:
      batchId ||
      o.lab_test_batch_id ||
      o.batch_id ||
      undefined,
    service: o.service_name || o.service || o.test_name || "-",
    cpt: o.medical_service_no ?? o.medical_service_id ?? "-",
    priority: normalizePriorityLabel(o.priority),
    doctor: o.doctor_name || o.doctor || "-",
    technician: o.technician_name || "-",
    ordered: parseOrderedTime(o.ordered_time || o.ordered),
    barcode: orderNo ? `BC-${orderNo}` : "-",
    sample: o.sample_name || o.sample || "-",
    status: normalizeLabOrderStatus(o.status),
    results: normalizeLabResultRows(
      o.results || o.test_results || o.parameters || o.lab_results,
    ),
    careContextReferenceId:
      o.care_context_reference_id || o.careContextReferenceId || null,
    careContextDisplay:
      o.care_context_display || o.service_name || o.test_name || null,
    medicalServiceId: o.medical_service_id || "",
    medicalServiceNo: o.medical_service_no ?? o.medical_service_id ?? null,
    labTestId: o.lab_test_id || null,
  };
};

/**
 * Flat OPD (or OPD-like) lab_test_list row with no nested orders[] —
 * synthesize one board order from top-level fields.
 */
export function mapFlatOpdEntryToOrder(item) {
  const orderNo = String(
    item.order_no ||
      (Array.isArray(item.orders) && item.orders[0]?.order_no) ||
      item.lab_test_id ||
      "",
  );
  const service = item.test_name || item.service_name || "-";
  return mapApiOrder(
    {
      order_no: orderNo,
      service_name: service,
      medical_service_id: item.medical_service_id,
      medical_service_no: item.medical_service_no,
      priority: item.priority,
      doctor_name: item.doctor_name || item.doctor_name_unregistered,
      ordered_time: item.ordered_time || item.appointment_start_time,
      sample_name: item.sample_name,
      status: item.status,
      care_context_reference_id: item.care_context_id,
      care_context_display: service,
      lab_test_id: item.lab_test_id,
      lab_test_batch_id: item.lab_test_batch_id || item.lab_test_id,
    },
    { batchId: item.lab_test_id || item.batchId },
  );
}

// An order placed from the emergency department carries patient_type
// "EMERGENCY". It used to be folded into OPD here, which made an ED order
// indistinguishable from a walk-in — and OPD assumptions (a billed
// appointment, no bed) do not hold for one. Blank still means OPD: the column
// was added after those rows started being written.
const resolvePatientType = (item) => {
  const raw = item?.patient_type;
  if (raw == null || raw === "") return "OPD";
  const value = String(raw).toUpperCase();
  if (value === "IPD") return "IPD";
  if (value === "EMERGENCY") return "EMERGENCY";
  return "OPD";
};

const isMissingDemoValue = (value) =>
  value === undefined || value === null || value === "" || value === "-";

const LAB_STATUS_PROGRESS = [
  "Ordered",
  "Barcode Generated",
  "Sample Collected",
  "Sample Acknowledged",
  "Sample Sent",
  "Sample Received",
  "Processing",
  "Completed",
  "Reported",
  "Verified",
  "Cancelled",
];

const labStatusProgressRank = (status) => {
  const idx = LAB_STATUS_PROGRESS.indexOf(status);
  return idx === -1 ? 0 : idx;
};

/** Prefer the richer / further-along copy when the same order_no appears twice. */
const preferOrderCopy = (a, b) => {
  if (!a) return b;
  if (!b) return a;
  const rankA = labStatusProgressRank(a.status);
  const rankB = labStatusProgressRank(b.status);
  if (rankB !== rankA) return rankB > rankA ? b : a;
  let score = 0;
  const scoreOne = (o) => {
    let s = 0;
    if (o.sample && o.sample !== "-") s += 30;
    if (o.ordered) s += 20;
    if (o.cpt && o.cpt !== "-") s += 10;
    if (o.doctor && o.doctor !== "-") s += 5;
    if (o.medicalServiceNo != null) s += 5;
    return s;
  };
  score = scoreOne(b) - scoreOne(a);
  return score > 0 ? b : a;
};

const dedupeOrdersByOrderNo = (orders) => {
  const best = new Map();
  (orders || []).forEach((order) => {
    const key = String(order?.orderNo || "").trim();
    if (!key) return;
    best.set(key, preferOrderCopy(best.get(key), order));
  });
  return Array.from(best.values()).sort((a, b) =>
    String(a.orderNo).localeCompare(String(b.orderNo)),
  );
};

const mergePatientDemographics = (target, source) => {
  if (!source) return target;
  const fields = [
    "name",
    "uhid",
    "bedNo",
    "roomNo",
    "age",
    "gender",
    "dob",
    "phone",
    "abhaId",
    "doctorName",
    "appointmentMode",
    "appointmentStartTime",
    "patientId",
    "careContextId",
    "appointmentId",
  ];
  fields.forEach((key) => {
    if (isMissingDemoValue(target[key]) && !isMissingDemoValue(source[key])) {
      target[key] = source[key];
    }
  });
  if (!target.labTestId && source.labTestId) {
    target.labTestId = source.labTestId;
  }
  return target;
};

const mapLabTestListEntry = (item) => {
  const patientType = resolvePatientType(item);
  const labTestId = String(item.lab_test_id || "").trim();
  const admissionId = item.admission_id ?? null;
  const patientId = item.patient_id || "";
  const batchKey = labTestId || item.care_context_id || patientId || "row";

  const id =
    patientType === "IPD" && admissionId != null && admissionId !== ""
      ? String(admissionId)
      : `${patientId || "opd"}::${batchKey}`;

  const nestedOrders = Array.isArray(item.orders) ? item.orders : null;
  let orders;
  if (nestedOrders && nestedOrders.length > 0) {
    // Do NOT stamp batchId = lab_test_id on admission-wide orders[] — the API
    // repeats the full order list on every lab_test entry, and that stamp
    // forced false per-lab_test batch splits (duplicate patient cards).
    orders = nestedOrders.map((o) =>
      mapApiOrder({
        ...o,
        doctor_name:
          o.doctor_name || item.doctor_name || item.doctor_name_unregistered,
        lab_test_id: o.lab_test_id || labTestId || undefined,
      }),
    );
  } else {
    orders = [mapFlatOpdEntryToOrder(item)];
  }

  return {
    id: String(id),
    admissionId: patientType === "IPD" ? admissionId : null,
    patientId,
    labTestId: labTestId || null,
    appointmentId: item.appointment_id || null,
    careContextId: item.care_context_id || null,
    patientType,
    name: item.patient_name || "Patient",
    uhid: item.uhid || "-",
    bedNo: item.bed_no || item.bedNo || "-",
    roomNo: item.room_no || item.roomNo || "-",
    age: item.age ?? item.age_years ?? null,
    gender: item.gender || "-",
    dob: item.dob || "-",
    phone: item.patient_phone_no || item.phone || "-",
    abhaId: item.abha_id || null,
    doctorName: item.doctor_name || item.doctor_name_unregistered || "-",
    appointmentMode: item.appointment_mode || "",
    appointmentStartTime: item.appointment_start_time || "",
    orders,
  };
};

/**
 * Convert get_patient_lab_tests/ lab_test_list into board patient rows.
 * IPD entries that share an admission_id (each carrying the full orders[])
 * collapse into one patient; OPD stays one row per lab_test_id.
 */
export function transformUnifiedLabTestsToPatients(labTestList) {
  if (!Array.isArray(labTestList)) return [];

  const mapped = labTestList
    .filter((item) => item && typeof item === "object")
    .map(mapLabTestListEntry);

  const opdRows = [];
  const ipdByAdmission = new Map();

  mapped.forEach((row) => {
    const isIpd =
      row.patientType === "IPD" &&
      row.admissionId != null &&
      row.admissionId !== "";
    if (!isIpd) {
      opdRows.push(row);
      return;
    }

    const key = String(row.admissionId);
    const existing = ipdByAdmission.get(key);
    if (!existing) {
      ipdByAdmission.set(key, { ...row, id: key, orders: [...row.orders] });
      return;
    }
    mergePatientDemographics(existing, row);
    existing.orders = existing.orders.concat(row.orders);
  });

  const ipdRows = Array.from(ipdByAdmission.values()).map((patient) => {
    let orders = dedupeOrdersByOrderNo(patient.orders);
    orders = applyRememberedLabBatches(patient.admissionId, orders);
    orders = reconcileOrderBatchKeys(orders).sort((a, b) =>
      String(a.orderNo).localeCompare(String(b.orderNo)),
    );
    return { ...patient, orders };
  });

  // OPD rows may still need batch reconcile when they carry nested orders.
  const normalizedOpd = opdRows.map((patient) => ({
    ...patient,
    orders: reconcileOrderBatchKeys(dedupeOrdersByOrderNo(patient.orders)).sort(
      (a, b) => String(a.orderNo).localeCompare(String(b.orderNo)),
    ),
  }));

  return [...ipdRows, ...normalizedOpd];
}

/**
 * Build query params for GET get_patient_lab_tests/. Empty/null values omitted.
 * patientType "ALL" (or empty) omits patient_type so every kind is returned.
 */
export function buildLabTestsQueryParams({
  page = 1,
  pageSize = 25,
  patientType,
  search,
  status,
  priority,
  dateFrom,
  dateTo,
  orderedTime,
  admissionId,
  appointmentId,
  labTestId,
  orderNo,
  medicalServiceId,
} = {}) {
  const params = {};
  const pageNum = Number(page);
  const sizeNum = Number(pageSize);
  if (Number.isFinite(pageNum) && pageNum > 0) params.page = pageNum;
  if (Number.isFinite(sizeNum) && sizeNum > 0) params.page_size = sizeNum;

  const type = String(patientType || "").trim().toUpperCase();
  if (type === "IPD" || type === "OPD" || type === "EMERGENCY") {
    params.patient_type = type;
  }

  const q = String(search || "").trim();
  if (q) params.search = q;

  if (status && status !== "All statuses") params.status = status;

  if (priority && priority !== "All priorities") {
    params.priority = String(priority).trim().toLowerCase();
  }

  if (dateFrom) params.ordered_time_from = dateFrom;
  if (dateTo) params.ordered_time_to = dateTo;
  if (orderedTime) params.ordered_time = orderedTime;

  if (admissionId != null && admissionId !== "") {
    params.admission_id = String(admissionId);
  }
  if (appointmentId) params.appointment_id = String(appointmentId);
  if (labTestId) params.lab_test_id = String(labTestId);
  if (orderNo) params.order_no = String(orderNo);
  if (medicalServiceId) params.medical_service_id = String(medicalServiceId);

  return params;
}

/** Read pagination fields from a get_patient_lab_tests/ envelope. */
export function extractLabTestsPagination(data, fallbackPage = 1, fallbackSize = 25) {
  const page = Number(data?.page ?? data?.current_page ?? fallbackPage) || fallbackPage;
  const pageSize =
    Number(data?.page_size ?? data?.pageSize ?? fallbackSize) || fallbackSize;
  const totalCountRaw =
    data?.total_count ?? data?.totalCount ?? data?.count ?? data?.total;
  const totalCount =
    totalCountRaw == null || totalCountRaw === ""
      ? null
      : Number(totalCountRaw);
  return {
    page,
    pageSize,
    totalCount: Number.isFinite(totalCount) ? totalCount : null,
  };
}

/**
 * GET get_patient_lab_tests/ with auth headers.
 * Returns { patients, pagination, raw }.
 */
export async function fetchLabTestsPage(axios, authHeaders, query = {}, config = {}) {
  const params = buildLabTestsQueryParams(query);
  const res = await axios.get(
    `${process.env.REACT_APP_BACKEND_ROUTE_URL}get_patient_lab_tests/`,
    { headers: authHeaders, params, ...config },
  );
  if (res.data?.is_valid === false) {
    throw new Error(res.data?.message || "Request rejected by server");
  }
  // Prefer raw lab_test_list so nested orders[] survive; fall back to extractor.
  const list = Array.isArray(res.data?.lab_test_list)
    ? res.data.lab_test_list
    : extractPatientLabTestsFromResponse(res.data);
  const patients = transformUnifiedLabTestsToPatients(list);
  const pagination = extractLabTestsPagination(
    res.data,
    params.page || 1,
    params.page_size || 25,
  );
  return { patients, pagination, raw: res.data, list };
}

// Resolve sample type for a single order line (API sample_name preferred).
export const resolveOrderSampleLabel = (order) => {
  const sample = order?.sample;
  if (sample && sample !== "-") {
    const normalized = String(sample).toLowerCase().trim();
    if (normalized.includes("urine")) return "Urine";
    if (normalized.includes("blood")) return "Blood";
    if (normalized.includes("serum")) return "Serum";
    if (normalized.includes("plasma")) return "Plasma";
    return String(sample).trim();
  }
  const service = String(order?.service || "");
  if (/\b(urine|urinalysis)\b/i.test(service)) return "Urine";
  if (/\bblood\b/i.test(service)) return "Blood";
  return "-";
};

const normalizeSampleKey = (label) =>
  String(label || "-")
    .trim()
    .toLowerCase() || "-";

// One add_patient_lab_tests POST = one batch. Prefer an explicit batchId
// (from API or rememberLabOrderBatch). Only time-cluster orders that still
// have no batchId — never merge two different remembered saves together.
export const BATCH_MERGE_WINDOW_MS = 60000;

const orderedTimeMs = (ordered) => {
  if (!ordered) return NaN;
  const raw = String(ordered).trim();
  // Prefer the ISO-ish form mapApiOrder already normalizes to
  // ("2026-07-24T11:54:24" or full ISO with offset).
  const d = new Date(
    raw.length >= 19 && raw[10] === " "
      ? `${raw.slice(0, 10)}T${raw.slice(11)}`
      : raw,
  );
  if (!Number.isNaN(d.getTime())) return d.getTime();
  const sliced = raw.slice(0, 19);
  const d2 = new Date(sliced.includes("T") ? sliced : sliced.replace(" ", "T"));
  return d2.getTime();
};

const parseLabOrderSeq = (orderNo) => {
  const m = /^LAB-(\d{4})-(\d+)$/i.exec(String(orderNo || "").trim());
  if (!m) return null;
  return { year: m[1], n: Number(m[2]) };
};

const isConsecutiveLabOrder = (a, b) => {
  const sa = parseLabOrderSeq(a?.orderNo);
  const sb = parseLabOrderSeq(b?.orderNo);
  if (!sa || !sb || sa.year !== sb.year) return false;
  return Math.abs(sa.n - sb.n) === 1;
};

export const resolveOrderBatchKey = (order) => {
  if (order?.batchId) return String(order.batchId);
  const ts = String(order?.ordered || "").slice(0, 19);
  if (ts) return `batch-${ts}`;
  return `__single_${order?.orderNo || "unknown"}`;
};

const assignClusterBatchId = (clusterOrders) => {
  if (!clusterOrders?.length) return;
  const labOrder = clusterOrders.find((order) => /^LAB-/i.test(order.orderNo));
  const batchId = labOrder
    ? labOrder.orderNo
    : `batch-${String(clusterOrders[0].ordered || "").slice(0, 19)}`;
  clusterOrders.forEach((order) => {
    order.batchId = batchId;
  });
};

const clusterUnbatchedByTime = (unbatched) => {
  const sortable = unbatched
    .map((order) => ({ order, t: orderedTimeMs(order.ordered) }))
    .filter((row) => !Number.isNaN(row.t))
    .sort(
      (a, b) =>
        a.t - b.t ||
        String(a.order.orderNo).localeCompare(
          String(b.order.orderNo),
          undefined,
          {
            numeric: true,
          },
        ),
    );

  if (sortable.length === 0) {
    const byNo = [...unbatched].sort((a, b) =>
      String(a.orderNo).localeCompare(String(b.orderNo), undefined, {
        numeric: true,
      }),
    );
    let run = [];
    const flush = () => {
      if (run.length >= 2) assignClusterBatchId(run);
      run = [];
    };
    byNo.forEach((order) => {
      if (!run.length || isConsecutiveLabOrder(run[run.length - 1], order)) {
        run.push(order);
        return;
      }
      flush();
      run = [order];
    });
    flush();
    return;
  }

  const clusters = [];
  let current = null;
  sortable.forEach(({ order, t }) => {
    const prev = current?.orders[current.orders.length - 1];
    const withinWindow =
      current && t - current.endTime <= BATCH_MERGE_WINDOW_MS;
    const consecutive =
      prev && isConsecutiveLabOrder(prev, order) && withinWindow;
    if (!current || (!withinWindow && !consecutive)) {
      current = { orders: [order], endTime: t };
      clusters.push(current);
      return;
    }
    current.orders.push(order);
    current.endTime = t;
  });

  clusters.forEach((cluster) => {
    if (cluster.orders.length < 2) return;
    assignClusterBatchId(cluster.orders);
  });
};

// Collapse same-save orders into one shared batchId. Orders that already carry
// a batchId (remembered client save / API) are left alone so a later doctor
// save for the same patient stays on its own patient row.
export const reconcileOrderBatchKeys = (orders) => {
  const list = (orders || []).map((order) => ({ ...order }));
  const unbatched = list.filter((order) => !order.batchId);
  if (unbatched.length >= 2) {
    clusterUnbatchedByTime(unbatched);
  }
  return list;
};

const resolveBatchSampleLabel = (orders) => {
  if (!orders?.length) return "-";
  const labels = [...new Set(orders.map(resolveOrderSampleLabel))];
  return labels[0] || "-";
};

const resolveGroupBarcode = (orders, primaryOrderNo) => {
  const withBarcode = orders.find((o) => /^BC-LAB-/i.test(o.barcode));
  if (withBarcode) return withBarcode.barcode;
  const labOrder = orders.find((o) => /^LAB-/i.test(o.orderNo));
  if (labOrder) return labOrder.barcode || `BC-${labOrder.orderNo}`;
  if (orders.length === 1)
    return orders[0].barcode || `BC-${orders[0].orderNo}`;
  if (primaryOrderNo && /^LAB-/i.test(primaryOrderNo))
    return `BC-${primaryOrderNo}`;
  return orders[0]?.barcode || `BC-${orders[0]?.orderNo || "unknown"}`;
};

// Clusters orders from the same doctor batch into table rows, split by sample
// type so Urine and Blood tests never share one row even in the same LAB order.
export function groupOrdersByBatch(orders) {
  const reconciled = reconcileOrderBatchKeys(orders || []);
  const groups = [];
  const byGroupKey = new Map();

  reconciled.forEach((order) => {
    const batchKey = resolveOrderBatchKey(order);
    const sampleKey = normalizeSampleKey(resolveOrderSampleLabel(order));
    const groupKey = `${batchKey}|${sampleKey}`;
    let group = byGroupKey.get(groupKey);
    if (!group) {
      group = {
        batchKey,
        sampleKey,
        groupKey,
        sample: "-",
        barcode: order.barcode,
        primaryOrderNo: order.orderNo,
        orders: [],
      };
      byGroupKey.set(groupKey, group);
      groups.push(group);
    }
    if (/^BC-LAB-/i.test(order.barcode) && !/^BC-LAB-/i.test(group.barcode)) {
      group.barcode = order.barcode;
    }
    if (/^LAB-/i.test(order.orderNo)) {
      group.primaryOrderNo = order.orderNo;
      group.barcode = order.barcode || `BC-${order.orderNo}`;
    }
    group.orders.push(order);
  });

  groups.forEach((group) => {
    group.sample = resolveBatchSampleLabel(group.orders);
    const labOrder = group.orders.find((order) => /^LAB-/i.test(order.orderNo));
    group.primaryOrderNo = labOrder?.orderNo || group.orders[0]?.orderNo || "-";
    group.barcode = resolveGroupBarcode(group.orders, group.primaryOrderNo);
  });

  return groups.sort((a, b) =>
    String(a.orders[0]?.ordered || "").localeCompare(
      String(b.orders[0]?.ordered || ""),
    ),
  );
}

// Clusters an admission's orders into one group per order-placement batch —
// no sample-type split (unlike groupOrdersByBatch). Used to explode one
// patient into one top-level row per distinct doctor save, so a second save
// for the same patient shows up as its own "Patient" row instead of being
// folded into the first save's row.
export function groupOrdersByBatchOnly(orders) {
  const reconciled = reconcileOrderBatchKeys(orders || []);
  const groups = [];
  const byBatchKey = new Map();

  reconciled.forEach((order) => {
    const batchKey = resolveOrderBatchKey(order);
    let group = byBatchKey.get(batchKey);
    if (!group) {
      group = { batchKey, orders: [] };
      byBatchKey.set(batchKey, group);
      groups.push(group);
    }
    group.orders.push(order);
  });

  groups.forEach((group) => {
    const labOrder = group.orders.find((order) => /^LAB-/i.test(order.orderNo));
    group.primaryOrderNo = labOrder?.orderNo || group.orders[0]?.orderNo || "-";
    group.latestOrdered = group.orders.reduce(
      (latest, o) => (!latest || o.ordered > latest ? o.ordered : latest),
      null,
    );
  });

  return groups.sort((a, b) =>
    String(b.latestOrdered || "").localeCompare(String(a.latestOrdered || "")),
  );
}

// Backward-compatible alias used by barcode helpers.
export const groupOrdersBySample = groupOrdersByBatch;

export const STATUS_META = {
  Ordered: { bg: "#F3F4F6", color: "#374151" },
  "Barcode Generated": { bg: "#EEF2FF", color: "#4338CA" },
  "Sample Collected": { bg: "#FFF7ED", color: "#C2410C" },
  "Sample Acknowledged": { bg: "#ECFDF5", color: "#15803D" },
  "Sample Sent": { bg: "#ECFEFF", color: "#0E7490" },
  "Sample Received": { bg: "#EFF6FF", color: "#1D4ED8" },
  Processing: { bg: "#F3E8FF", color: "#7C3AED" },
  Completed: { bg: "#ECFDF5", color: "#15803D" },
  Reported: { bg: "#F0FDFA", color: "#0F766E" },
  Verified: { bg: "#F0FDFA", color: "#0F766E" },
  Cancelled: { bg: "#FEF2F2", color: "#B91C1C" },
};

export const PRIORITY_META = {
  STAT: { bg: "#FEF2F2", color: "#B91C1C" },
  Urgent: { bg: "#FFFBEB", color: "#B45309" },
  Routine: { bg: "#F3F4F6", color: "#374151" },
};

// Reg. no. / qualification shown in the "Verified By" block of a generated
// report — keyed by the doctor name already present on each order.
export const DOCTOR_CREDENTIALS = {
  "Dr. Vikram Iyer": { qualification: "MD (Pathology)", regNo: "98765" },
  "Dr. Meera Nair": { qualification: "MD (Pathology)", regNo: "84213" },
};

const istParts = (d, options) => {
  const map = {};
  new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Kolkata", ...options })
    .formatToParts(d)
    .forEach((p) => {
      if (p.type !== "literal") map[p.type] = p.value;
    });
  return map;
};

export const formatDateTime = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  const p = istParts(d, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
  const period = (p.dayPeriod || "").toUpperCase();
  return `${p.day}/${p.month}/${p.year}, ${p.hour}:${p.minute}:${p.second} ${period}`.trim();
};

export const computeAge = (dob) => {
  if (!dob) return null;
  const birth = new Date(dob);
  if (Number.isNaN(birth.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const monthDiff = now.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < birth.getDate()))
    age -= 1;
  return age;
};

export function StatusPill({ status }) {
  const m = STATUS_META[status] || { bg: "#F3F4F6", color: "#374151" };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 10px",
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 700,
        background: m.bg,
        color: m.color,
        whiteSpace: "nowrap",
      }}
    >
      {status}
    </span>
  );
}

export function PriorityPill({ priority }) {
  const m = PRIORITY_META[priority] || PRIORITY_META.Routine;
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 9px",
        borderRadius: 6,
        fontSize: 11,
        fontWeight: 700,
        background: m.bg,
        color: m.color,
        whiteSpace: "nowrap",
      }}
    >
      {priority}
    </span>
  );
}
